import { useEffectPlease } from "@features/utils";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface AudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob) => void;
  onClear?: () => void;
  disabled?: boolean;
}

let cachedAudioBlob: Blob | null = null;

/**
 * Convert audio blob to WAV format for better backend compatibility
 */
const convertToWav = async (audioBlob: Blob): Promise<Blob> => {
  const audioContext = new AudioContext();
  const arrayBuffer = await audioBlob.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  // Create WAV file
  const numberOfChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;

  const bytesPerSample = bitDepth / 8;
  const blockAlign = numberOfChannels * bytesPerSample;

  const samples = audioBuffer.length;
  const dataSize = samples * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // WAV header
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, format, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  // Write audio data
  const channelData: Float32Array[] = [];
  for (let i = 0; i < numberOfChannels; i++) {
    channelData.push(audioBuffer.getChannelData(i));
  }

  let offset = 44;
  for (let i = 0; i < samples; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, channelData[channel][i]));
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
  }

  await audioContext.close();
  return new Blob([buffer], { type: "audio/wav" });
};

export const AudioRecorder = ({
  onRecordingComplete,
  onClear,
  disabled = false,
}: AudioRecorderProps) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasRecording, setHasRecording] = useState(!!cachedAudioBlob);
  const [audioUrl, setAudioUrl] = useState<string | null>(
    cachedAudioBlob ? URL.createObjectURL(cachedAudioBlob) : null
  );

  useEffectPlease(() => {
    if (!!cachedAudioBlob) {
      onRecordingComplete(cachedAudioBlob);
    }
  }, []);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const webmBlob = new Blob(chunksRef.current, { type: "audio/webm" });

        // Convert to WAV for better backend compatibility
        try {
          const wavBlob = await convertToWav(webmBlob);
          cachedAudioBlob = wavBlob;
          const url = URL.createObjectURL(wavBlob);
          setAudioUrl(url);
          setHasRecording(true);
          onRecordingComplete(wavBlob);
        } catch (error) {
          console.error("Failed to convert to WAV, using webm:", error);
          // Fallback to webm if conversion fails
          cachedAudioBlob = webmBlob;
          const url = URL.createObjectURL(webmBlob);
          setAudioUrl(url);
          setHasRecording(true);
          onRecordingComplete(webmBlob);
        }

        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Failed to start recording:", error);
    }
  }, [onRecordingComplete]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  const clearRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setHasRecording(false);
    setRecordingTime(0);
    onClear?.();
  }, [audioUrl, onClear]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [audioUrl]);

  if (hasRecording && audioUrl) {
    return (
      <Flex direction="column" gap="2">
        <audio src={audioUrl} controls className="w-full h-8" />
        <Button
          size="1"
          variant="soft"
          color="red"
          onClick={clearRecording}
          disabled={disabled}
        >
          {t("components.audio_recorder.clear")}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex align="center" gap="2">
      {isRecording ? (
        <>
          <Button
            size="2"
            color="red"
            onClick={stopRecording}
            disabled={disabled}
          >
            <span className="w-2 h-2 bg-white rounded-sm animate-pulse" />
            {t("components.audio_recorder.stop")}
          </Button>
          <Text size="2" color="red" className="font-mono">
            {formatTime(recordingTime)}
          </Text>
        </>
      ) : (
        <Button
          size="2"
          variant="soft"
          onClick={startRecording}
          disabled={disabled}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
          </svg>
          {t("components.audio_recorder.record")}
        </Button>
      )}
    </Flex>
  );
};
